import Vue from 'vue';
import axios from 'axios';
import * as auth from '@/utils/auth.js';

export default class Api {
  /**
   * @param {Object} options={}
   */
  constructor(options = {}) {
    this.client =
      options.client ||
      axios.create({
        baseURL: options.baseUrl,
        timeout: 300000
      });

    if (options.lang) {
      this.client.defaults.headers.common['X-LANG'] = process.env.LANGUAGE;
    }

    this.token = options.token;
    this.refreshToken = options.refreshToken;
    this.refreshRequest = null;
    this.options = {
      ...options
    };

    this.client.interceptors.request.use(
      config => {
        localStorage.removeItem('login-form');
        if (!this.token) return config;
        if (this.options.authRequired) {
          const newConfig = {
            ...config
          };

          newConfig.headers.Authorization = `Bearer ${this.token}`;
          return newConfig;
        }
        return config;
      },
      e => Promise.reject(e)
    );

    this.client.interceptors.response.use(
      r => r,
      async error => {
        if (error instanceof axios.Cancel) throw error;

        if (error.response && error.config.url.includes('auth/refresh-token')) {
          localStorage.setItem('login-form', 'true');
          Vue.api.logout();
          return;
        }

        if (
          !this.refreshToken &&
          error.response &&
          error.response.status === 401
        ) {
          Vue.api.logout();
          return;
        }

        if (
          !this.refreshToken ||
          (error.response && error.response.status !== 401) ||
          error.config.retry ||
          error.response === undefined
        ) {
          throw error;
        }

        if (!this.refreshRequest) {
          Vue.api.isRefreshing = true;
          this.refreshRequest = this.client
            .put(`${process.env.AUTH_API_HOST}/auth/refresh-token`, {
              refreshToken: this.refreshToken
            })
            .then(response => {
              this.refreshRequest = null;
              this.token = response.data.accessToken;
              this.refreshToken = response.data.refreshToken;
              if (auth.readUser()) {
                Vue.api.updateCredentials({
                  token: this.token,
                  refreshToken: this.refreshToken,
                  email: auth.readUser()
                });
              }
              newRequest.headers.Authorization = `Bearer ${this.token}`;
              Vue.api.queue = Vue.api.queue.map(callback => {
                if (typeof callback === 'function') {
                  callback(this.token);
                }
              });
              Vue.api.isRefreshing = false;
            });
        }

        const newRequest = {
          ...error.config,
          retry: true
        };

        if (Vue.api.isRefreshing) {
          return new Promise(resolve => {
            Vue.api.queue.push(accessToken => {
              newRequest.headers.Authorization = 'Bearer ' + accessToken;
              resolve(this.client(newRequest));
            });
          });
        }

        if (auth.readUser()) {
          Vue.api.updateCredentials({
            token: this.token,
            refreshToken: this.refreshToken,
            email: auth.readUser()
          });
        }

        return this.client(newRequest);
      }
    );
  }

  async login({ login, password }) {
    const { data } = await this.client.post(
      `${process.env.AUTH_API_HOST}/auth/login`,
      { login, password }
    );
    this.token = data.accessToken;
    this.refreshToken = data.refreshToken;

    return { token: data.accessToken, refreshToken: data.refreshToken };
  }

  logout() {
    this.token = null;
    this.refreshToken = null;
  }

  async get(url, params = {}) {
    return this.client
      .get(url, { ...params })
      .then(({ data }) => data)
      .catch(e => Promise.reject(e));
  }

  async post(url, params = {}) {
    return this.client
      .post(url, { ...params })
      .then(({ data }) => data)
      .catch(e => Promise.reject(e));
  }

  async put(url, params = {}) {
    return this.client
      .put(url, { ...params })
      .then(({ data }) => data)
      .catch(e => Promise.reject(e));
  }

  async delete(url, params = {}) {
    return this.client
      .delete(url, { ...params })
      .then(({ data }) => data)
      .catch(e => Promise.reject(e));
  }
}
